import {
  hasTmaInitData,
  waitForTmaInitData,
} from '@common/services/tmaInitDataService';
import { fetcher } from '@common/utils/fetcher';

const TMA_APP_INSTANCE_NAME = 'kiss-date-tma';
const APP_INSTANCE_ID_STORAGE_KEY = 'app_instance_id';

type StartTmaResponse = {
  appInstance?: {
    id?: string;
  };
};

let appInstanceIdCache: string | null = null;
let startPromise: Promise<string> | null = null;

const readStoredTmaAppInstanceId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(APP_INSTANCE_ID_STORAGE_KEY);
};

const storeTmaAppInstanceId = (appInstanceId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(APP_INSTANCE_ID_STORAGE_KEY, appInstanceId);
};

const clearStoredTmaAppInstanceId = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(APP_INSTANCE_ID_STORAGE_KEY);
};

const requestTmaAppInstanceId = async (): Promise<string> => {
  const initData = await waitForTmaInitData();

  if (!initData) {
    throw new Error('No supported app instance surface detected');
  }

  const response = await fetcher<StartTmaResponse>({
    url: '/start/tma',
    method: 'POST',
    data: {
      name: TMA_APP_INSTANCE_NAME,
      initData,
    },
    auth: null,
  });

  const appInstanceId = response?.appInstance?.id;

  if (!appInstanceId) {
    throw new Error('Start TMA response missing app instance id');
  }

  appInstanceIdCache = appInstanceId;
  storeTmaAppInstanceId(appInstanceId);
  return appInstanceId;
};

const startTmaAppInstanceRequest = (): Promise<string> => {
  startPromise = requestTmaAppInstanceId().finally(() => {
    startPromise = null;
  });

  return startPromise;
};

export const appInstanceService = {
  getCurrentPaymentProvider: (): 'telegram_stars' | null => {
    if (hasTmaInitData()) {
      return 'telegram_stars';
    }

    return null;
  },

  getCachedTmaAppInstanceId: () => {
    if (appInstanceIdCache) {
      return appInstanceIdCache;
    }

    const storedAppInstanceId = readStoredTmaAppInstanceId();

    if (storedAppInstanceId) {
      appInstanceIdCache = storedAppInstanceId;

      return storedAppInstanceId;
    }

    return null;
  },

  clearCachedTmaAppInstanceId: () => {
    appInstanceIdCache = null;
    clearStoredTmaAppInstanceId();
  },

  getOrCreateCurrentAppInstanceId: async (): Promise<string> => {
    const cached = appInstanceService.getCachedTmaAppInstanceId();
    if (cached) {
      return cached;
    }

    if (startPromise) {
      return startPromise;
    }

    return startTmaAppInstanceRequest();
  },

  getOrCreateTmaAppInstanceId: async (): Promise<string> => {
    return appInstanceService.getOrCreateCurrentAppInstanceId();
  },

  refreshTmaAppInstanceId: async (): Promise<string> => {
    appInstanceService.clearCachedTmaAppInstanceId();

    return startTmaAppInstanceRequest();
  },
};
